import { EventLocationType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/event_location_type_pb'
import { LockIcon, SquareArrowTopRightIcon } from '@fingertip/icons'
import { useTranslation } from 'react-i18next'

import { TooltipWrapper } from '@/components/shared/tooltip-wrapper'
import { normalizePhoneNumber } from '@/lib/utils/utils'

const isGoogleMeetUrl = (url: string): boolean => {
  const pattern = /^https:\/\/meet\.google\.com\/[a-z\-]+$/
  return pattern.test(url)
}

const isZoomMeetingUrl = (url: string): boolean => {
  const pattern =
    /^https:\/\/([a-z0-9]+\.)*zoom\.us\/j\/\d+\?pwd=[a-zA-Z0-9]+(\.[0-9]+)?$/
  return pattern.test(url)
}

type Props = {
  location?: string
  locationType?: EventLocationType
  hideDetails?: boolean
}

const getLocationTypeName = (locationType: EventLocationType): string => {
  switch (locationType) {
    case EventLocationType.HOST_IN_PERSON:
      return 'Host In Person'
    case EventLocationType.ATTENDEE_IN_PERSON:
      return 'Attendee In Person'
    case EventLocationType.HOST_PHONE:
      return 'Host Phone'
    case EventLocationType.ATTENDEE_PHONE:
      return 'Attendee Phone'
    case EventLocationType.CONFERENCING:
      return 'Conferencing Link'
    case EventLocationType.LINK:
      return 'Link/URL'
    default:
      return 'Unknown'
  }
}

export const BookingLocation = ({
  location,
  locationType,
  hideDetails,
}: Props) => {
  const { t } = useTranslation()
  const finalLocation = location?.trim()

  if (hideDetails && locationType) {
    return (
      <div className="flex flex-row items-start gap-1 text-muted-foreground">
        <div>{getLocationTypeName(locationType)}</div>
        <TooltipWrapper message={'Revealed upon confirming'}>
          <LockIcon className="size-5" />
        </TooltipWrapper>
      </div>
    )
  }

  if (
    locationType === EventLocationType.CONFERENCING &&
    isGoogleMeetUrl(finalLocation || '')
  ) {
    return (
      <a
        className="flex flex-row items-center gap-2 underline opacity-70"
        href={finalLocation}
      >
        {t('google_meet')} <SquareArrowTopRightIcon className="size-4" />
      </a>
    )
  }

  if (
    locationType === EventLocationType.CONFERENCING &&
    isZoomMeetingUrl(finalLocation || '')
  ) {
    return (
      <a
        className="flex flex-row items-center gap-2 underline opacity-70"
        href={finalLocation}
      >
        {t('zoom_meeting')} <SquareArrowTopRightIcon className="size-4" />
      </a>
    )
  }

  if (
    locationType === EventLocationType.LINK ||
    locationType === EventLocationType.CONFERENCING
  ) {
    return (
      <a
        className="flex flex-row items-center gap-2 underline opacity-70"
        href={finalLocation}
      >
        <span>{finalLocation}</span>{' '}
        <SquareArrowTopRightIcon className="size-4" />
      </a>
    )
  }

  if (
    locationType === EventLocationType.HOST_PHONE ||
    locationType === EventLocationType.ATTENDEE_PHONE
  ) {
    return (
      <>
        {locationType === EventLocationType.HOST_PHONE && (
          <p>{t('attendee_calls')}</p>
        )}
        {locationType === EventLocationType.ATTENDEE_PHONE && (
          <p>{t('host_calls')}</p>
        )}
        <a
          className="flex flex-row items-center gap-2 underline opacity-70"
          href={normalizePhoneNumber(finalLocation || '')}
        >
          {finalLocation}
        </a>
      </>
    )
  }

  if (
    locationType === EventLocationType.ATTENDEE_IN_PERSON ||
    locationType === EventLocationType.HOST_IN_PERSON
  ) {
    return (
      <>
        {locationType === EventLocationType.ATTENDEE_IN_PERSON && (
          <p>{t('hosts_address')}</p>
        )}
        {locationType === EventLocationType.HOST_IN_PERSON && (
          <p>{t('attendees_address')}</p>
        )}
        <a
          className="flex flex-row items-center gap-2 underline opacity-70"
          title="View in maps"
          href={`https://maps.google.com/?q=${finalLocation}`}
        >
          {finalLocation}
        </a>
      </>
    )
  }

  return <>{finalLocation}</>
}
